import React from 'react';
import { ITrendPanel } from '../Dashboard.model';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';
import { useTheme } from '@mui/material';

// Hook to track container size
function useResizeObserver(ref: React.RefObject<HTMLElement>) {
  const [size, setSize] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    if (!ref.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setSize({ width, height });
      }
    });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}

interface TrendPanelProps {
  panel: ITrendPanel;
}
export default function TrendPanel(props: TrendPanelProps) {
  const { panel } = props;
  return (
    <StatCard
      title={panel.title}
      subtitle={panel.subtitle}
      data={[10, 20, 15, 30, 25, 40, 35]}
    />
  );
}

export type StatCardProps = {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  data: number[];
};

function AreaGradient({ id }: { id: string }) {
  const theme = useTheme();
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop
          offset="0%"
          stopColor={theme.palette.primary.main}
          stopOpacity={0.3}
        />
        <stop
          offset="100%"
          stopColor={theme.palette.primary.main}
          stopOpacity={0}
        />
      </linearGradient>
    </defs>
  );
}

export function StatCard({ title, subtitle, data }: StatCardProps) {
  const theme = useTheme();
  const containerRef = React.useRef(null);
  const { width, height } = useResizeObserver(containerRef);

  return (
    <>
      <Stack direction="column" alignItems="flex-start" width={'100%'}>
        <Typography variant="body1" fontWeight={800} fontSize={'0.8em'}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={'0.6em'}>
          {subtitle}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        width={'100%'}
        height={'100%'}
      >
        <Box
          ref={containerRef}
          sx={{
            flexGrow: 1,
            height: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
          }}
        >
          <SparkLineChart
            data={data}
            area
            showHighlight
            showTooltip
            width={width}
            height={height || 60}
            color={theme.palette.primary.main}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              [`& .${areaElementClasses.root}`]: {
                fill: `url(#area-gradient-${title})`,
              },
            }}
          >
            <AreaGradient id={`area-gradient-${title}`} />
          </SparkLineChart>
        </Box>
      </Stack>
    </>
  );
}
